import { TFunction } from "i18next";
import {
  AstroAngle,
  AstroCityHit,
  AstroFeatureProperties,
} from "../../../../models/interfaces";
import {
  ANGLE_LABEL_KEYS,
  FALLBACK_LINE_COLOR,
} from "./astrocartographyConstants";
import {
  getLineLabel,
  getPlanetGlyph,
  normaliseAngle,
  pickInterpretation,
} from "./astrocartographyUtils";

/**
 * Leaflet popups take DOM nodes, not React trees. These builders assemble the
 * popup bodies with `textContent` so backend-supplied copy is never injected as
 * markup; the visual styling lives in the map component's global styles.
 */

const createElement = (
  tag: string,
  className?: string,
  text?: string,
): HTMLElement => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
};

export const buildLinePopup = (
  properties: AstroFeatureProperties,
  language: string,
  t: TFunction,
): HTMLElement => {
  const container = createElement("div", "astro-popup");
  const interpretation = pickInterpretation(properties, language);

  const heading = createElement("div", "astro-popup-heading");
  const swatch = createElement("span", "astro-popup-swatch");
  swatch.style.background = properties.color ?? FALLBACK_LINE_COLOR;
  heading.appendChild(swatch);
  heading.appendChild(
    createElement(
      "span",
      "astro-popup-title",
      interpretation?.title ??
        `${getPlanetGlyph(properties.planet)} ${getLineLabel(properties)}`.trim(),
    ),
  );
  container.appendChild(heading);

  if (interpretation?.tone_label) {
    const isHarmonious =
      properties.harmony === "harmonious" || interpretation.tone === "harmonious";
    container.appendChild(
      createElement(
        "span",
        `astro-popup-chip ${isHarmonious ? "is-harmonious" : "is-challenging"}`,
        interpretation.tone_label,
      ),
    );
  }

  const angle = normaliseAngle(properties.line ?? properties.angle);
  const angleKey = ANGLE_LABEL_KEYS[angle as AstroAngle];
  if (angleKey) {
    container.appendChild(
      createElement("div", "astro-popup-meta", t(angleKey) as string),
    );
  }

  if (interpretation?.paragraphs?.length) {
    interpretation.paragraphs.forEach((paragraph) =>
      container.appendChild(createElement("p", undefined, paragraph)),
    );
  } else {
    container.appendChild(
      createElement(
        "p",
        "astro-popup-empty",
        t("astroNoInterpretation") as string,
      ),
    );
  }

  if (interpretation?.note) {
    container.appendChild(
      createElement("p", "astro-popup-note", interpretation.note),
    );
  }

  return container;
};

export const buildCityPopup = (
  city: AstroCityHit,
  planet: string,
  t: TFunction,
): HTMLElement => {
  const container = createElement("div", "astro-popup");
  container.appendChild(
    createElement(
      "div",
      "astro-popup-heading",
      `${city.name} (${city.countryCode})`,
    ),
  );
  const angleKey = ANGLE_LABEL_KEYS[city.angle as AstroAngle];
  container.appendChild(
    createElement(
      "div",
      "astro-popup-meta",
      `${getPlanetGlyph(planet)} ${planet} · ${angleKey ? (t(angleKey) as string) : city.angle}`.trim(),
    ),
  );
  container.appendChild(
    createElement(
      "div",
      "astro-popup-meta",
      t("astroDistanceFromLine", {
        distance: Math.round(city.distanceKm),
      }) as string,
    ),
  );
  return container;
};
